import { Row } from '@tanstack/react-table';

import { isLoadMoreRow } from '../../../helpers/approval-load-more-row.helper';
import { isApprovalTimeOffEntry } from '../../../helpers/approval-row-model.helper';
import {
  ApprovalMemberRow,
  ApprovalProjectRow,
  ApprovalTimeEntry,
  ApprovalTimeOffEntry,
} from '../../../types/time-tracking-approvals';

/**
 * The approvals table is a three-level tree rendered through a single
 * `ApprovalProjectRow` column model:
 *   depth 0 → project row, depth 1 → member row, depth 2 → time-entry row.
 *
 * Row depth is the source of truth for the row kind (informational time-off rows also
 * live at depth 2 — check `isTimeOffRow` BEFORE the worklog branch). The member/entry
 * shapes are surfaced to the table via casts (TanStack types every row as the top-level
 * model), so these helpers centralize both the depth checks and the casts to
 * keep the unsafe `as unknown as` in one place.
 */
export const isMemberRow = (row: Row<ApprovalProjectRow>) => row.depth === 1;
export const isTimeEntryRow = (row: Row<ApprovalProjectRow>) => row.depth === 2;

export const asMemberRow = (row: Row<ApprovalProjectRow>) =>
  row.original as unknown as ApprovalMemberRow;
export const asTimeEntry = (row: Row<ApprovalProjectRow>) =>
  row.original as unknown as ApprovalTimeEntry;

/** True if the row is an informational time-off day (leaf level, never selectable/approvable). */
export const isTimeOffRow = (row: Row<ApprovalProjectRow>) => isApprovalTimeOffEntry(row.original);
export const asTimeOffEntry = (row: Row<ApprovalProjectRow>) =>
  row.original as unknown as ApprovalTimeOffEntry;

/** True if the row is a synthetic "loading more" sentinel (renders a spinner, no data). */
export const isLoadMoreTableRow = (row: Row<ApprovalProjectRow>) => isLoadMoreRow(row.original);
